'use client';

import { forwardRef, useCallback, useEffect, useRef } from 'react';

import { clsx } from 'clsx';

import type { ChirpIllustrationProps } from './illustration.types';
import './styles/styles.scss';
import { loadIllustration, validateAccessibility } from './utils';

/**
 * A standalone React component that loads SVG illustrations from the CDN
 * and inlines them for theme customization via design tokens.
 */
export const ChirpIllustration = forwardRef<SVGSVGElement, ChirpIllustrationProps>(
  (
    {
      name,
      env = 'PRD',
      version = 'latest',
      isDecorative = false,
      colorScheme = 'auto',
      'aria-label': ariaLabel,
      className,
      onLoad,
      ...restProps
    },
    forwardedRef
  ) => {
    const svgRef = useRef<SVGSVGElement | null>(null);

    // Validate accessibility props in development
    validateAccessibility(isDecorative, ariaLabel);

    // Merge internal ref with forwarded ref
    const setRefs = useCallback(
      (element: SVGSVGElement | null) => {
        svgRef.current = element;
        if (typeof forwardedRef === 'function') {
          forwardedRef(element);
        } else if (forwardedRef) {
          forwardedRef.current = element;
        }
      },
      [forwardedRef]
    );

    // Fetch and inline the SVG
    useEffect(() => {
      const svgElement = svgRef.current;
      if (!svgElement) return;

      // Clear previous illustration content to ensure clean slate
      svgElement.innerHTML = '';

      loadIllustration({
        svgElement,
        name,
        env,
        version,
        onLoad
      });
    }, [name, env, version, onLoad]);

    if (isDecorative) {
      return (
        <svg
          ref={setRefs}
          {...restProps}
          aria-hidden="true"
          className={clsx('chirp-illustration', className)}
          data-a11y="decorative"
          data-color-scheme={colorScheme === 'auto' ? undefined : colorScheme}
          focusable="false"
          role="presentation"
        />
      );
    }

    return (
      <svg
        ref={setRefs}
        {...restProps}
        aria-label={ariaLabel}
        className={clsx('chirp-illustration', className)}
        data-color-scheme={colorScheme === 'auto' ? undefined : colorScheme}
        focusable="false"
        role="img"
      />
    );
  }
);

ChirpIllustration.displayName = 'ChirpIllustration';
